import { useCallback, useEffect, useId, useLayoutEffect, useRef, type JSX, type KeyboardEvent, type ReactNode } from 'react';
import { Button } from 'react-aria-components/Button';
import { Dialog } from 'react-aria-components/Dialog';
import { Modal, ModalOverlay } from 'react-aria-components/Modal';

export interface AccessibleDialogProps {
  isOpen: boolean;
  onOpenChange(open: boolean): void;
  title: string;
  children: ReactNode;
  closeLabel?: string;
  showCloseButton?: boolean;
  showTitle?: boolean;
  isDismissable?: boolean;
  isKeyboardDismissDisabled?: boolean;
  placement?: 'center' | 'end';
  size?: 'normal' | 'wide';
  id?: string;
  /**
   * `alertdialog` en vez del `dialog` genérico: para una confirmación que
   * interrumpe a exigir una decisión (W3C WAI-ARIA APG, "Alert and Message
   * Dialogs"). En la práctica no alcanzó para que NVDA 2026.2/Chrome entre
   * en modo foco al abrirse — confirmado con `aria-modal="true"` puesto y
   * el fondo con `aria-hidden`, con el HTML real inspeccionado a mano, así
   * que no es un problema de marcado.
   *
   * `application`: todo lo de adentro (Tab, flechas, Escape) ya lo
   * manejamos con JavaScript propio, así que perder la navegación de modo
   * exploración de NVDA ahí adentro no pierde nada real — y evita del
   * todo la heurística de cambio de modo que le está fallando. El panel
   * deja de ser un `<section>` (React Aria lo renderiza así por defecto) y
   * pasa a un `<div>` con `role="application"` puesto a mano — la
   * etiqueta HTML `<section>` es justo lo que NVDA anunciaba como
   * "sección" al entrar, más allá de qué rol ARIA tuviera puesto. Se usa
   * puntualmente en los diálogos que reportaron el problema, no en todos:
   * uno simple igual entra en modo foco solo con `alertdialog`.
   */
  role?: 'dialog' | 'alertdialog' | 'application';
}

/**
 * Diálogo modal común del sitio. React Aria aporta la contención del foco,
 * Escape, ocultamiento del fondo y retorno al control que lo abrió.
 */
export function AccessibleDialog({
  isOpen,
  onOpenChange,
  title,
  children,
  closeLabel = 'Cerrar',
  showCloseButton = true,
  showTitle = true,
  isDismissable = false,
  isKeyboardDismissDisabled = false,
  placement = 'center',
  size = 'normal',
  id,
  role = 'dialog',
}: AccessibleDialogProps) {
  const generatedId = useId();
  const dialogId = id ?? generatedId;
  const titleId = `${dialogId}-title`;
  const openerRef = useRef<HTMLElement | null>(null);
  const wasOpenRef = useRef(false);
  const panelRef = useRef<HTMLElement | null>(null);

  // React Aria omite `aria-modal` a propósito (lo dice `useDialog`: un bug
  // de Safari con VoiceOver dentro de un iframe, que acá no aplica) y confía
  // solo en que el fondo quede con `aria-hidden`. NVDA no siempre alcanza a
  // registrar ese límite con eso solo, y puede devolver el cursor virtual al
  // documento de fondo al abrirse el diálogo — de ahí que se pierda el modo
  // foco. `aria-modal` no es una de las props que React Aria deja pasar al
  // DOM (`filterDOMProps` la descarta), así que se agrega a mano sobre el
  // nodo real; React nunca la toca porque no la gestiona.
  const setDialogNode = useCallback((node: HTMLElement | null) => {
    panelRef.current = node;
    node?.setAttribute('aria-modal', 'true');
    // Mismo motivo que `aria-modal`: `role` tampoco lo deja pasar como
    // prop de React Aria cuando vale 'application' (solo tipa 'dialog' |
    // 'alertdialog').
    if (role === 'application') node?.setAttribute('role', 'application');
  }, [role]);

  // React Aria ya tiene un remedio parecido para un bug de VoiceOver/Safari:
  // enfocar el diálogo, esperar medio segundo, y volver a enfocarlo (ver
  // `useDialog`, comentario sobre iOS). Pero ese remedio solo corre si el
  // foco inicial quedó en el propio contenedor del diálogo — acá el primer
  // foco cae en un hijo (la pregunta, o un botón, según el contenido), así
  // que nunca se dispara. NVDA puede perder de vista el límite del modal
  // justo en la carrera entre el `aria-hidden` que oculta el fondo y ese
  // primer foco;
  // repetirlo un instante después, cuando esa carrera ya terminó, le da una
  // segunda oportunidad de registrar el modo foco sin tocar a quién esté
  // enfocado en ese momento (las flechas del diálogo pueden haberlo movido).
  useEffect(() => {
    if (!isOpen) return;
    const timeout = window.setTimeout(() => {
      const active = document.activeElement;
      if (active instanceof HTMLElement && panelRef.current?.contains(active)) {
        active.blur();
        active.focus();
      }
    }, 400);
    return () => window.clearTimeout(timeout);
  }, [isOpen]);

  // Los diálogos controlados no siempre viven dentro de un DialogTrigger.
  // Conservamos el origen aquí para que todos, también los abiertos por un
  // cambio de estado, cumplan el mismo retorno de foco.
  //
  // Efecto de layout, no el pasivo de siempre: el `autoFocus` de lo que hay
  // adentro (la pregunta, o un botón) también corre en un efecto, y los
  // efectos de layout de todo el árbol terminan antes que empiece a correr
  // ningún efecto pasivo — así se captura quién estaba enfocado antes de
  // abrir, nunca lo que el diálogo acababa de enfocar por su cuenta.
  useLayoutEffect(() => {
    if (isOpen && !wasOpenRef.current) {
      openerRef.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    } else if (!isOpen && wasOpenRef.current) {
      const opener = openerRef.current;
      window.setTimeout(() => {
        if (opener?.isConnected) opener.focus();
      }, 0);
    }
    wasOpenRef.current = isOpen;
  }, [isOpen]);

  return (
    <ModalOverlay
      className={`accessible-dialog-overlay accessible-dialog-overlay--${placement}`}
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      isDismissable={isDismissable}
      isKeyboardDismissDisabled={isKeyboardDismissDisabled}
    >
      <Modal className={`accessible-dialog-modal accessible-dialog-modal--${size}`}>
        <Dialog
          ref={setDialogNode}
          id={dialogId}
          role={role === 'application' ? 'dialog' : role}
          className="accessible-dialog-panel"
          {...(showTitle ? { 'aria-labelledby': titleId } : { 'aria-label': title })}
          // React Aria renderiza el panel como `<section>` — esa misma
          // etiqueta HTML es lo que NVDA anunciaba como "sección" al
          // entrar, más allá de qué rol ARIA se le pusiera encima (ya
          // probamos pisar el rol a mano sobre el `<section>` real, sin
          // cambiar la etiqueta: seguía sonando igual). `render` deja
          // elegir la etiqueta real; la documentación de React Aria pide
          // no cambiarla, pero acá no depende de nada específico de
          // `<section>` — es contenido propio, no un componente de
          // terceros que dé por sentada esa etiqueta.
          {...(role === 'application'
            ? { render: (domProps: JSX.IntrinsicElements['section']) => <div {...(domProps as JSX.IntrinsicElements['div'])} /> }
            : {})}
        >
          {showTitle ? (
            <div className="d-flex justify-content-between align-items-center gap-2 mb-2">
              <h2 id={titleId} className="h5 mb-0">{title}</h2>
              {showCloseButton ? (
                <Button slot="close" className="btn-close" aria-label={closeLabel} />
              ) : null}
            </div>
          ) : null}
          {children}
        </Dialog>
      </Modal>
    </ModalOverlay>
  );
}

interface AccessibleConfirmDialogProps {
  isOpen: boolean;
  onOpenChange(open: boolean): void;
  title: string;
  children: ReactNode;
  confirmLabel: string;
  onConfirm(): void;
  cancelLabel?: string;
  isPending?: boolean;
  danger?: boolean;
  /** Presentación breve para confirmaciones simples de una partida. */
  compact?: boolean;
  /** Ver `AccessibleDialogProps.role`. */
  role?: 'dialog' | 'alertdialog' | 'application';
}

/** Confirmación que enfoca primero la pregunta, nunca directamente una acción. */
export function AccessibleConfirmDialog({
  isOpen,
  onOpenChange,
  title,
  children,
  confirmLabel,
  onConfirm,
  cancelLabel = 'Cancelar',
  isPending = false,
  danger = true,
  compact = false,
  role,
}: AccessibleConfirmDialogProps) {
  const questionRef = useRef<HTMLDivElement>(null);

  // El `autoFocus` de JSX no alcanza acá: solo lo aplican de forma
  // confiable los controles de formulario (botón, input...), no un `div`
  // cualquiera con `tabIndex`. Se enfoca a mano en cada apertura.
  useEffect(() => {
    if (isOpen) questionRef.current?.focus();
  }, [isOpen]);

  // Tab ya recorre el texto y los dos botones (lo da React Aria más el
  // `tabIndex` del texto, abajo); esto suma flecha izquierda/derecha como
  // recorrido adicional entre las tres paradas —incluida la pregunta, no
  // solo los botones—, sin reemplazar Tab. Mismo criterio que el resto
  // del sitio con listas navegables.
  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (!['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(event.key)) return;
    const stops = Array.from(event.currentTarget.querySelectorAll<HTMLElement>('[data-dialog-stop]'));
    const currentIndex = stops.indexOf(document.activeElement as HTMLElement);
    if (currentIndex === -1) return;
    event.preventDefault();
    const delta = event.key === 'ArrowRight' || event.key === 'ArrowDown' ? 1 : -1;
    const next = stops[(currentIndex + delta + stops.length) % stops.length];
    next?.focus();
  };

  return (
    <AccessibleDialog
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      title={title}
      showTitle={!compact}
      showCloseButton={false}
      isKeyboardDismissDisabled={isPending}
      role={role}
    >
      <div onKeyDown={handleKeyDown}>
        {/* `tabIndex=0` y `autoFocus`: se lee la pregunta antes que
          * cualquier botón — quien pregunta espera enterarse de qué se le
          * está preguntando antes de que le ofrezcan una respuesta— y
          * queda como una parada más para volver a leerla con Tab o con
          * las flechas, sin tener que cerrar y reabrir.
          *
          * Probamos `role="document"` acá para que NVDA leyera esto como
          * prosa en vez de anunciarlo como "sección" — resultado peor: al
          * anidar `document` dentro de la `application` del diálogo, NVDA
          * vuelve a armar su propio buffer de exploración para este
          * pedazo, exactamente el mecanismo que le hacía perder el modo
          * foco (por eso existe `application` acá). Revertido: sin rol
          * propio, aunque anuncie "sección". */}
        <div
          ref={questionRef}
          className="accessible-dialog-body"
          tabIndex={0}
          data-dialog-stop
        >
          {children}
        </div>
        <div className="accessible-dialog-actions">
          {compact ? (
            <>
              <Button data-dialog-stop className={danger ? 'btn btn-outline-danger' : 'btn btn-accent'} isPending={isPending} onPress={onConfirm}>
                {confirmLabel}
              </Button>
              <Button data-dialog-stop className="btn btn-outline-primary" isDisabled={isPending} onPress={() => onOpenChange(false)}>
                {cancelLabel}
              </Button>
            </>
          ) : (
            <>
              <Button data-dialog-stop className="btn btn-outline-primary" isDisabled={isPending} onPress={() => onOpenChange(false)}>
                {cancelLabel}
              </Button>
              <Button data-dialog-stop className={danger ? 'btn btn-outline-danger' : 'btn btn-accent'} isPending={isPending} onPress={onConfirm}>
                {confirmLabel}
              </Button>
            </>
          )}
        </div>
      </div>
    </AccessibleDialog>
  );
}
